import React, { useEffect, useState, useContext } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Container,
  Table,
  Spinner,
  Row,
  Col,
  Badge,
  Form,
  InputGroup,
  InputGroupText,
  Input,
  FormGroup,
  Button
} from "reactstrap";
import { ThemeContext } from "contexts/ThemeContext";
import { useNavigate } from "react-router-dom";
import { serverHost } from "variables/serverdata";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";

const DealerDashboard = ({ userData }) => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const [overview, setOverview] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [search, setSearchTerm] = useState("");
  const navigate = useNavigate();
  // Load user info
  let storedUser = {};
  try {
    storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  } catch (err) {
    console.error("Failed to parse stored user:", err);
  }

  const dealerId = userData?.dealer_uuid || storedUser?.dealer_uuid;
  const role = userData?.role || storedUser?.role;

  const pagination = paginationFactory({
    page: 1,
    alwaysShowAllBtns: true,
    showTotal: true,
    withFirstAndLast: false,
    sizePerPageRenderer: ({ onSizePerPageChange }) => (
      <div className="dataTables_length">
        <label>
          Show{" "}
          <select
            className="form-control form-control-sm"
            onChange={(e) => onSizePerPageChange(e.target.value)}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>{" "}
          entries.
        </label>
      </div>
    ),
  });

  const columns = [
    {
      dataField: "company_name",
      text: "Client",
      formatter: (cell) => (
        <span className="fw-semibold" style={{ fontSize: "1.1rem" }}>
          {cell}
        </span>
      ),
    },
    {
      dataField: "sites",
      text: "Sites",
      formatter: (sites, row) => {
        if (!sites || sites.length === 0) {
          return <span className="text-muted">No sites</span>;
        }

        return (
          <ul className="mb-0 list-unstyled">
            {sites.map((site) => (
              <li
                key={site.site_uuid}
                style={{
                  fontSize: "1rem",
                  cursor: "pointer",
                  color: isDark ? "#38bdf8" : "#0ea5e9",
                  padding: "4px 6px",
                  borderRadius: "4px",
                  width: "fit-content",
                }}
                onClick={() => {
                  const siteWithClient = {
                    ...site,
                    client_uuid: row.client_uuid,
                  };

                  localStorage.setItem(
                    "selectedSite",
                    JSON.stringify(siteWithClient)
                  );
                  localStorage.setItem(
                    "clientSites",
                    JSON.stringify(row.sites)
                  );
                  localStorage.setItem("clientId", row.client_uuid);

                  window.dispatchEvent(
                    new CustomEvent("siteChanged", { detail: site })
                  );

                  navigate(
                    role === "SALES"
                      ? "/admin/subscription"
                      : "/admin/home",
                    { state: { siteId: site.site_uuid } }
                  );
                }}
              >
                <i className="ni ni-pin-3 text-muted me-1" />
                {site.company_name} — {site.city}, {site.country}
              </li>
            ))}
          </ul>
        );
      },
    },
  ];

  // Auth check
  useEffect(() => {
    if (
      !dealerId ||
      (role !== "GLOBAL" && role !== "SALES" && role !== "TECH")
    ) {
      navigate("/auth/login", { replace: true });
    } else {
      setAuthorized(true);
    }
  }, [dealerId, role, navigate]);

  // Fetch data
  useEffect(() => {
    if (!authorized) return;

    const fetchOverview = async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${serverHost}/dealer_overview?dealer_uuid=${dealerId}`,
        );
        if (!res.ok) throw new Error(`API request failed: ${res.status}`);
        const data = await res.json();
        setOverview(data.dealer_overview || []);
      } catch (err) {
        console.error("Error fetching dealer overview:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchOverview();
  }, [dealerId, authorized]);

  const filteredRows = React.useMemo(() => {
    if (!search) return overview;

    const searchLower = search.toLowerCase();

    return overview.filter((row) => {
      // match company name
      const companyMatch = String(row.company_name ?? "")
        .toLowerCase()
        .includes(searchLower);

      // match any site name
      const siteMatch = Array.isArray(row.sites) &&
        row.sites.some((site) =>
          String(site.company_name ?? "")
            .toLowerCase()
            .includes(searchLower)
        );

      return companyMatch || siteMatch;
    });
  }, [overview, search]);

  const totalClients = overview.length;
  const totalSites = overview.reduce(
    (acc, c) => acc + (c.sites?.length || 0),
    0,
  );



  return (
    <Container
      fluid
      className="mt--5 px-4"
      style={{
        minHeight: "85vh",
        maxWidth: "1400px",
        margin: "0 auto",
      }}
    >
      {loading ? (
        <div className="text-center mt-5">
          <Spinner color="primary" />
        </div>
      ) : (
        <>
          {/* Header */}
          <div
            className="d-flex justify-content-between align-items-center mb-4"
            style={{
              borderBottom: `1px solid ${isDark ? "#1f2937" : "#e5e7eb"}`,
              paddingBottom: "0.75rem",
            }}
          >
            <div>
              <h2
                className="fw-bold mb-1"
                style={{
                  color: isDark ? "#f9fafb" : "#111827",
                }}
              >
                Dealer Overview
              </h2>
              <div
                style={{
                  height: "3px",
                  width: "100%",
                  borderRadius: "3px",
                  background: "linear-gradient(90deg, #0ea5e9, #14b8a6)",
                }}
              />
            </div>
            <div>
              <Button
                className="calls-dashboard-nav-button"
                title="View all active calls in the system"
                onClick={() => navigate("/admin/dealer-menu")}
              >
                <i class="fas fa-boxes" style={{color: "rgb(229, 185, 255)"}}></i> Dealer Menu <i className="fas fa-angle-right"></i>
              </Button>
              <Button
                className="calls-dashboard-nav-button"
                title="View all active calls in the system"
                onClick={() => navigate("/admin/calls-overview")}
              >
                <i class="fas fa-phone-alt" style={{color: "rgb(185, 225, 255)"}}></i> Calls Dashboard <i className="fas fa-angle-right"></i>
              </Button>
            </div>
            {/*<Badge
              pill
              style={{
                fontSize: "1.2rem",
                padding: "0.6rem 1.2rem",
                color: isDark ? "#f9fafb" : "#111827",
                background: "linear-gradient(135deg, #0ea5e9, #14b8a6)",
                boxShadow: isDark
                  ? "0 6px 18px rgba(0,0,0,0.6)"
                  : "0 6px 18px rgba(0,0,0,0.12)",
              }}
            >
              {totalClients} Clients • {totalSites} Sites
            </Badge>*/}
          </div>

          {/* Stat Cards */}
          <Row className="gy-3 gx-3 mb-4">
            {[
              {
                label: "Total Clients",
                value: totalClients,
                accent: isDark ? "#38bdf8" : "#0ea5e9",
              },
              {
                label: "Total Sites",
                value: totalSites,
                accent: isDark ? "#2dd4bf" : "#14b8a6",
              },
            ].map((stat, idx) => (
              <Col key={idx} md="6" xl="3">
                <Card
                  className="border-0"
                  style={{
                    height: "100%",
                    borderRadius: "0.75rem",
                    backgroundColor: isDark ? "#020617" : "#ffffff",
                    color: isDark ? "#e5e7eb" : "#111827",
                    boxShadow: isDark
                      ? "0 8px 24px rgba(0,0,0,0.6)"
                      : "0 8px 24px rgba(0,0,0,0.1)",
                    transition: "transform 0.15s ease, box-shadow 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-3px)";
                    e.currentTarget.style.boxShadow = isDark
                      ? "0 12px 30px rgba(0,0,0,0.75)"
                      : "0 12px 30px rgba(0,0,0,0.15)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = isDark
                      ? "0 8px 24px rgba(0,0,0,0.6)"
                      : "0 8px 24px rgba(0,0,0,0.1)";
                  }}
                >
                  <CardBody className="text-center py-4">
                    <h6
                      className="text-uppercase text-muted mb-2"
                      style={{ fontSize: "1rem" }}
                    >
                      {stat.label}
                    </h6>
                    <h2
                      className="fw-bold mb-0"
                      style={{
                        color: stat.accent,
                        fontSize: "2.4rem",
                        letterSpacing: "-0.5px",
                      }}
                    >
                      {stat.value}
                    </h2>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
          {/* Clients Table */}
          <Card
            className="border-0"
            style={{
              borderRadius: "0.75rem",
              width: "100%",
              flexGrow: 1,
              overflow: "hidden",
              backgroundColor: isDark ? "#020617" : "#ffffff",
              color: isDark ? "#e5e7eb" : "#111827",
              boxShadow: isDark
                ? "0 10px 30px rgba(0,0,0,0.7)"
                : "0 10px 30px rgba(0,0,0,0.12)",
            }}
          >
            <CardHeader
              className="d-flex justify-content-between align-items-center flex-wrap header-theme border-0"
              style={{
                padding: "1rem 1.5rem",
                backgroundColor: isDark ? "#01030f" : "#f9fafb",
                borderBottom: `1px solid ${isDark ? "#020617" : "#e5e7eb"}`,
              }}
            >
              <div className="d-flex align-items-center">
                <CardTitle
                  tag="h4"
                  className="mb-0 fw-semibold"
                  style={{
                    color: isDark ? "#f9fafb" : "#111827",
                  }}
                >
                  Clients & Sites
                </CardTitle>
              </div>
              <div className="d-flex gap-2 flex-wrap align-items-center">
                <Form className="navbar-search navbar-search-light form-inline">
                  <FormGroup className="mb-0">
                    <InputGroup className="input-group-alternative input-group-merge search-border">
                      <InputGroupText>
                        <i className="fas fa-search search-icon" />
                      </InputGroupText>
                      <Input
                        className="form-control table-search-bar"
                        placeholder="Search"
                        value={search}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </InputGroup>
                  </FormGroup>
                </Form>
              </div>
            </CardHeader>

            <CardBody className="p-0">
              <BootstrapTable
                keyField="client_uuid"
                data={filteredRows}
                columns={columns}
                bootstrap4
                bordered={false}
                hover
                pagination={pagination}
                classes="align-items-center table-flush"
                noDataIndication={() =>
                  loading ? (
                    <div className="text-center">Loading...</div>
                  ) : (
                    <div className="text-center">No clients found.</div>
                  )
                }
              />
            </CardBody>
          </Card>
        </>
      )}
    </Container>
  );
};

export default DealerDashboard;
